// src/components/TermCheckCard.jsx
// 学期の登録ミスを見つける【2026-11-04】（年度の切り替えの画面の1段）
//   GET   /api/schedule/year-rollover/term-check?year=        登録した学期と、実際にやった日がずれている授業
//   PATCH /api/roster-imports/classes/:id/year { termId, dryRun }  学期を直す（同じ名前の授業があれば止まる）
//   「このままでよい」は、この端末のこの画面でだけ覚える（授業と学期の組で）
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Notice, { toast } from './Notice';
import { confirmDialog } from './ConfirmDialog';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' });
const md = (s) => { if (!s) return ''; const [, m, d] = s.split('-'); return `${Number(m)}/${Number(d)}`; };
const okKey = (c) => `termcheck-ok:${c.id}:${c.term.id}`;
const readOk = (c) => { try { return localStorage.getItem(okKey(c)) === '1'; } catch (_) { return false; } };
const writeOk = (c, v) => { try { if (v) localStorage.setItem(okKey(c), '1'); else localStorage.removeItem(okKey(c)); } catch (_) { /* 覚えられなくても困らない */ } };

export default function TermCheckCard({ year, isStaff, no }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);       // 直している授業の id
  const [showOk, setShowOk] = useState(false);  // 「このままでよい」にした授業も出す
  const [, setTick] = useState(0);

  const load = useCallback(async () => {
    setErr(''); setData(null);
    try {
      const r = await fetch('/api/schedule/year-rollover/term-check?year=' + year, { headers: auth() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) throw new Error(j.message || '読み込めませんでした');
      setData(j);
    } catch (e) { setErr(e.message); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const all = data ? data.classes : [];
  const okCount = all.filter(readOk).length;
  const list = showOk ? all : all.filter(c => !readOk(c));   // 「このままでよい」を押すたびに描き直すので、毎回数える

  const fix = async (c) => {
    const to = c.suggested;
    if (!to) return;
    setBusy(c.id);
    try {
      const url = `/api/roster-imports/classes/${c.id}/year`;
      const body = { academicYear: year, termId: to.id };
      const pr = await fetch(url, { method: 'PATCH', headers: auth(), body: JSON.stringify({ ...body, dryRun: true }) });
      const pj = await pr.json().catch(() => ({}));
      if (!pr.ok || !pj.success) throw new Error(pj.message || '確かめられませんでした');
      const step = (pj.plan && pj.plan[0]) || {};
      if (step.action === 'conflict' || step.action === 'blocked') {
        throw new Error((step.warnings && step.warnings[0]) || step.reason || 'このままでは直せません。取り込みの履歴の画面で直してください');
      }
      const warn = (step.warnings || []).join('\n');
      const go = await confirmDialog(
        `「${c.name}」の学期を ${c.term.name} から ${to.name} に変えます。出席の記録はそのままです。` + (warn ? '\n\n' + warn : ''),
        { title: `${year}年度${to.name}にします`, okLabel: '変える', danger: false });
      if (!go) return;
      const r = await fetch(url, { method: 'PATCH', headers: auth(), body: JSON.stringify(body) });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) throw new Error(j.message || '変えられませんでした');
      toast.success(j.message || '変えました');
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(null); }
  };

  const keep = (c, v) => { writeOk(c, v); setTick(t => t + 1); };

  return (
    <li className="card yr-step">
      <div className="yr-step-h"><span className="yr-no">{no}</span><h2>授業の学期を確かめる</h2></div>
      <p>登録した学期と、実際に授業をした日・出席のあった日がずれている授業を探します（{year}年度・今日までの分）。</p>
      <Notice kind="error">{err}</Notice>
      {!data && !err && <p style={{ color: '#6b7280' }}>読み込み中…</p>}
      {data && list.length === 0 && (
        <p style={{ color: '#15803d', fontWeight: 600 }}>
          ずれている授業はありません。{okCount > 0 && <small style={{ color: '#6b7280', fontWeight: 400 }}>（「このままでよい」にした {okCount}件を除く）</small>}
        </p>
      )}
      {list.length > 0 && (
        <>
          <p>学期の外に授業をした日がある授業が <b>{list.length}件</b> あります。学期の前後{data.graceDays}日と、補講1回だけのものは数えていません。</p>
          <div className="table-wrap">
            <table className="data-table yr-table">
              <thead><tr><th>授業</th>{isStaff && <th>先生</th>}<th>登録の学期</th><th>実際にした日</th><th></th></tr></thead>
              <tbody>
                {list.map(c => (
                  <tr key={c.id}>
                    <td><Link to={`/classes/${c.id}/schedule`}>{c.name}</Link>{c.closed ? <small style={{ color: '#b45309' }}>（締め済み）</small> : null}</td>
                    {isStaff && <td>{c.teacherName || '—'}</td>}
                    <td>{c.term.name}<br /><small style={{ color: '#6b7280' }}>{md(c.term.start)}〜{md(c.term.end)}</small></td>
                    <td>
                      {md(c.first)}〜{md(c.last)}（{c.days}日）<br />
                      <small style={{ color: '#b45309' }}>学期の外 {c.outside}日：{md(c.outsideFirst)}{c.outside > 1 ? `〜${md(c.outsideLast)}` : ''}</small>
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {c.suggested
                        ? <button type="button" className="btn-primary" disabled={busy !== null || c.closed} onClick={() => fix(c)}>
                            {busy === c.id ? '変えています…' : `${c.suggested.name}にする`}
                          </button>
                        : <Link className="btn-secondary" to="/roster-imports">取り込みの履歴で直す</Link>}
                      {' '}
                      {readOk(c)
                        ? <button type="button" className="btn-secondary" onClick={() => keep(c, false)}>戻す</button>
                        : <button type="button" className="btn-secondary" disabled={busy !== null} onClick={() => keep(c, true)}>このままでよい</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {okCount > 0 && (
        <p className="yr-note">
          <label><input type="checkbox" checked={showOk} onChange={e => setShowOk(e.target.checked)} /> 「このままでよい」にした {okCount}件も出す（この端末だけで覚えています）</label>
        </p>
      )}
    </li>
  );
}
